/**
 * useRegistry — OutpostForge frontend (CES standard)
 * Outpost registrations for the Settings page: list, add (server-validated
 * against the owner account) and remove.
 */

import { useCallback, useEffect, useState } from 'react';
import { describeApiError } from '../services/apiErrors';
import { settingsService, type SettingsService } from '../services/api/settingsService';
import type { OutpostRegistration, RegisterOutpostRequest } from '../types/outposts';

export function useRegistry(service: SettingsService = settingsService) {
  const [registrations, setRegistrations] = useState<OutpostRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const response = await service.getRegistrations();
      setRegistrations(response.registrations);
      setError(null);
    } catch (err) {
      setError(describeApiError(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Register an Outpost; returns an error message instead of throwing. */
  const add = useCallback(
    async (input: RegisterOutpostRequest): Promise<string | null> => {
      try {
        await service.registerOutpost(input);
        await reload();
        return null;
      } catch (err) {
        return describeApiError(err);
      }
    },
    [service, reload],
  );

  const remove = useCallback(
    async (outpostId: string): Promise<string | null> => {
      try {
        await service.removeOutpost(outpostId);
        await reload();
        return null;
      } catch (err) {
        return describeApiError(err);
      }
    },
    [service, reload],
  );

  return { registrations, loading, error, add, remove, reload };
}
